// Minimal, dependency-free Markdown for the agent chat bubbles.
//
// The model emits a small Markdown subset — bold/italic, inline code, links,
// headings, lists, fenced code and quotes. We parse it into a plain tree and
// render that as React nodes, so model output is never injected as HTML.
//
// Deliberately not a full CommonMark implementation: it covers what the agent
// actually writes, and anything it doesn't recognise degrades to literal text.

export type MarkdownInline =
  | { type: "text"; value: string }
  | { type: "strong"; children: MarkdownInline[] }
  | { type: "em"; children: MarkdownInline[] }
  | { type: "code"; value: string }
  | { type: "link"; href: string; children: MarkdownInline[] }
  | { type: "br" };

export type MarkdownBlock =
  | { type: "paragraph"; children: MarkdownInline[] }
  | { type: "heading"; level: number; children: MarkdownInline[] }
  | { type: "list"; ordered: boolean; items: MarkdownInline[][] }
  | { type: "codeBlock"; value: string; language: string | null }
  | { type: "quote"; children: MarkdownInline[] }
  | { type: "rule" };

// Only these schemes become real links; anything else (javascript:, data:, …)
// stays as plain text.
const SAFE_LINK = /^(https?:|mailto:)/i;
const LINK_RE = /^\[([^\]]*)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/;

const HEADING_RE = /^(#{1,6})\s+(.*)$/;
const FENCE_RE = /^\s*```(.*)$/;
const RULE_RE = /^\s*([-*_])\1{2,}\s*$/;
const QUOTE_RE = /^\s*>\s?(.*)$/;
const UL_RE = /^\s*[-*+]\s+(.*)$/;
const OL_RE = /^\s*\d+[.)]\s+(.*)$/;

const isBlockStart = (line: string): boolean =>
  FENCE_RE.test(line) ||
  RULE_RE.test(line) ||
  HEADING_RE.test(line) ||
  QUOTE_RE.test(line) ||
  UL_RE.test(line) ||
  OL_RE.test(line);

// Emphasised spans must not be padded with whitespace (`* text *` is literal).
const isTight = (value: string): boolean =>
  value.length > 0 && value.trim() === value && !value.includes("\n");

export const parseInline = (source: string): MarkdownInline[] => {
  const nodes: MarkdownInline[] = [];
  let buffer = "";
  let i = 0;

  const flush = () => {
    if (buffer) {
      nodes.push({ type: "text", value: buffer });
      buffer = "";
    }
  };

  while (i < source.length) {
    const char = source[i];

    if (char === "`") {
      const end = source.indexOf("`", i + 1);
      if (end > i + 1) {
        flush();
        nodes.push({ type: "code", value: source.slice(i + 1, end) });
        i = end + 1;
        continue;
      }
    }

    if (char === "\n") {
      flush();
      nodes.push({ type: "br" });
      i += 1;
      continue;
    }

    if (char === "[") {
      const match = LINK_RE.exec(source.slice(i));
      if (match && SAFE_LINK.test(match[2])) {
        flush();
        nodes.push({
          type: "link",
          href: match[2],
          children: parseInline(match[1]),
        });
        i += match[0].length;
        continue;
      }
    }

    if ((char === "*" || char === "_") && source[i + 1] === char) {
      const end = source.indexOf(char + char, i + 2);
      const inner = end > i + 2 ? source.slice(i + 2, end) : "";
      if (end > i + 2 && isTight(inner)) {
        flush();
        nodes.push({ type: "strong", children: parseInline(inner) });
        i = end + 2;
        continue;
      }
    }

    if (char === "*" || char === "_") {
      const end = source.indexOf(char, i + 1);
      const inner = end > i + 1 ? source.slice(i + 1, end) : "";
      if (end > i + 1 && isTight(inner)) {
        flush();
        nodes.push({ type: "em", children: parseInline(inner) });
        i = end + 1;
        continue;
      }
    }

    buffer += char;
    i += 1;
  }

  flush();
  return nodes;
};

export const parseMarkdown = (source: string): MarkdownBlock[] => {
  const lines = source.replace(/\r\n?/g, "\n").split("\n");
  const blocks: MarkdownBlock[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) {
      i += 1;
      continue;
    }

    const fence = FENCE_RE.exec(line);
    if (fence) {
      const language = fence[1].trim() || null;
      const code: string[] = [];
      i += 1;
      while (i < lines.length && !FENCE_RE.test(lines[i])) {
        code.push(lines[i]);
        i += 1;
      }
      i += 1; // closing fence (or end of input)
      blocks.push({ type: "codeBlock", value: code.join("\n"), language });
      continue;
    }

    if (RULE_RE.test(line)) {
      blocks.push({ type: "rule" });
      i += 1;
      continue;
    }

    const heading = HEADING_RE.exec(line);
    if (heading) {
      blocks.push({
        type: "heading",
        level: heading[1].length,
        children: parseInline(heading[2].trim()),
      });
      i += 1;
      continue;
    }

    if (QUOTE_RE.test(line)) {
      const quote: string[] = [];
      while (i < lines.length && QUOTE_RE.test(lines[i])) {
        quote.push(QUOTE_RE.exec(lines[i])![1]);
        i += 1;
      }
      blocks.push({ type: "quote", children: parseInline(quote.join("\n")) });
      continue;
    }

    const ordered = OL_RE.test(line);
    if (ordered || UL_RE.test(line)) {
      const items: MarkdownInline[][] = [];
      while (i < lines.length) {
        const item = ordered ? OL_RE.exec(lines[i]) : UL_RE.exec(lines[i]);
        if (!item) break;
        items.push(parseInline(item[1].trim()));
        i += 1;
      }
      blocks.push({ type: "list", ordered, items });
      continue;
    }

    const paragraph = [line.trim()];
    i += 1;
    while (i < lines.length && lines[i].trim() && !isBlockStart(lines[i])) {
      paragraph.push(lines[i].trim());
      i += 1;
    }
    blocks.push({ type: "paragraph", children: parseInline(paragraph.join("\n")) });
  }

  return blocks;
};
